import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getMySpendOptions } from "@/api";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section/QuerySection";
import { StatRow } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { formatCompact, formatDate, formatNumber } from "@/i18n/format";
import { BudgetStatus } from "./BudgetStatus";
import { MonthlyTrend } from "./MonthlyTrend";
import { CostByFeature, CostByModel } from "./SpendBreakdowns";
import { formatCost, TREND_MONTHS, utcMonthStart } from "./spend";

export function UsagePage() {
  const { t } = useTranslation("settings");
  const budget = useCurrentUser().data?.budget;
  const spend = useQuery({ ...getMySpendOptions({ query: { months: TREND_MONTHS } }), select: (r) => r.data });
  const monthStart = utcMonthStart();

  return (
    <Page
      title={t("usage.title")}
      subtitle={t("usage.subtitle", { date: formatDate(monthStart, { dateStyle: "medium", timeZone: "UTC" }) })}
    >
      {budget && <BudgetStatus budget={budget} />}
      <QuerySection query={spend}>
        {(s) => (
          <>
            <StatRow
              stats={[
                {
                  label: t("usage.stats.cost", { month: formatDate(monthStart, { month: "short", timeZone: "UTC" }) }),
                  value: formatCost(s.currentMonthCostUsd),
                },
                { label: t("usage.stats.requests"), value: formatNumber(s.currentMonthRequests) },
                { label: t("usage.stats.tokensIn"), value: formatCompact(s.currentMonthTokensInput) },
                { label: t("usage.stats.tokensOut"), value: formatCompact(s.currentMonthTokensOutput) },
              ]}
            />
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <CostByModel rows={s.byModel ?? []} />
              <CostByFeature rows={s.byFeature ?? []} />
            </div>
            <MonthlyTrend trend={s.monthlyTrend ?? []} />
            <Text variant="caption" tone="tertiary">
              {t("usage.disclaimer")}
            </Text>
          </>
        )}
      </QuerySection>
    </Page>
  );
}
